import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Lock } from 'lucide-react';
import { FormField, PrimaryButton, SecondaryButton, PasswordTooltip, ErrorAlert } from '../../../shared/components';
import { useChangePassword } from '../hooks';
import { useValidation } from '../../../shared/hooks';
import { usePasswordStrength } from '../../auth/hooks';

/**
 * ChangePasswordModal Component
 * What: Modal for changing user password with current password verification
 * Why: Provides secure password change functionality with proper validation
 * Where: features/account/components/ChangePasswordModal.jsx
 * 
 * Features:
 * - Current password verification
 * - New password with strength requirements
 * - Password confirmation
 * - On-blur validation with submit-time final pass
 * - Autocomplete disabled for security
 * - Password visibility toggles
 */
export const ChangePasswordModal = ({ onClose }) => {
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    current_password: '',
    password: '',
    password_confirmation: ''
  });

  // Centralized validation hook
  const { errors, validateFieldOnBlur, validateForm, clearFieldError, clearAllErrors } = useValidation();
  
  // Password strength hook for new password
  const { strength: passwordStrength, showTooltip: showPasswordRequirements, handlePasswordFocus, handlePasswordBlur } = usePasswordStrength(formData.password);
  
  // React Query hook for password change with form reset callback
  const { mutate: changePassword, isPending, error, reset } = useChangePassword(() => {
    // Reset form fields when API error occurs
    setFormData({
      current_password: '',
      password: '',
      password_confirmation: ''
    });
    // Clear validation errors
    clearAllErrors();
  });

  // Handle clicks outside password field to close tooltip
  useEffect(() => {
    const handleClickOutside = (event) => {
      const passwordField = document.querySelector('input[name="password"]');
      const tooltip = document.querySelector('.password-tooltip');
      
      if (passwordField && !passwordField.contains(event.target) && 
          tooltip && !tooltip.contains(event.target)) {
        handlePasswordBlur();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handlePasswordBlur]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Use centralized validation for form submission (including password strength)
    const { isValid } = validateForm(formData, { strength: passwordStrength });
    
    if (!isValid) {
      return; // Validation errors are already set by validateForm
    }
    
    // Block submit if any existing validation errors remain
    const hasValidationErrors = Object.values(errors).some(error => error && error.trim() !== '');
    if (hasValidationErrors) {
      return; // Don't submit if there are validation errors
    }
    
    // Call React Query mutation
    changePassword(formData);
    
    // Close modal on success (handled in useChangePassword onSuccess)
    // The modal will be closed automatically when the user data is updated
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear React Query errors when user types
    reset();
    
    // Clear validation errors for this field
    clearFieldError(name);

    // No real-time validation; handled on blur
  };

  const handleFieldBlur = (e) => {
    const { name, value } = e.target;
    validateFieldOnBlur(name, value);
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-left">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Change Password
        </h3>
        <p className="text-sm text-gray-600">
          Enter your current password and choose a new one to keep your account secure.
        </p>
      </div>

      {/* General Error Display */}
      <ErrorAlert
        error={error?.response?.data?.message || error?.message}
        onClose={() => reset()}
        show={!!error && !error?.response?.data?.errors}
        className="mb-4"
      />

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Current Password Field */}
        <FormField
          label=""
          name="current_password"
          type={showCurrentPassword ? 'text' : 'password'}
          value={formData.current_password}
          onChange={handleChange}
          onBlur={handleFieldBlur}
          placeholder="Current Password"
          required
          autoComplete="off"
          error={errors.current_password || error?.response?.data?.errors?.current_password}
          icon={<Lock className="h-5 w-5" />}
          rightIcon={
            <button
              type="button"
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              {showCurrentPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          }
          className="[&_input]:text-sm [&_label]:text-xs [&_.text-sm]:text-xs"
        />

        {/* Divider between current and new password fields */}
        <div className="py-2">
          <hr className="border-gray-200" />
        </div>

        {/* New Password Field */}
        <div className="relative">
          <FormField
            label=""
            name="password"
            type={showNewPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleChange}
            onBlur={handleFieldBlur}
            placeholder="New Password"
            required
            autoComplete="off"
            error={errors.password || error?.response?.data?.errors?.password}
            icon={<Lock className="h-5 w-5" />}
            rightIcon={
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            }
            onFocus={handlePasswordFocus}
            className="[&_input]:text-sm [&_label]:text-xs [&_.text-sm]:text-xs"
          />
          
          {/* Password Requirements Tooltip */}
          <PasswordTooltip
            passwordStrength={passwordStrength}
            isVisible={showPasswordRequirements}
          />
        </div>



        {/* Confirm New Password Field */}
        <FormField
          label=""
          name="password_confirmation"
          type={showConfirmPassword ? 'text' : 'password'}
          value={formData.password_confirmation}
          onChange={handleChange}
          onBlur={handleFieldBlur}
          placeholder="Confirm New Password"
          required
          autoComplete="off"
          error={errors.password_confirmation || error?.response?.data?.errors?.password_confirmation}
          icon={<Lock className="h-5 w-5" />}
          rightIcon={
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          }
          className="[&_input]:text-sm [&_label]:text-xs [&_.text-sm]:text-xs"
        />

        {/* Action Buttons */}
        <div className="flex space-x-3 pt-4">
            <SecondaryButton
            type="button"
            onClick={handleCancel}
            className="flex-1"
            disabled={isPending}
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            className="flex-1"
            disabled={isPending || Object.values(errors).some(error => error && error.trim() !== '')}
          >
            {isPending ? 'Updating...' : 'Confirm'}
          </PrimaryButton>
        </div>
      </form>
    </div>
  );
};
